import type { AppListenerMiddleware } from "@src/store/rootState";
import { getAiChatSessionId } from "@src/store/slices/aiChat";
import { selectedExperience } from "@src/store/slices/experiences";
import { getAiChatProps, setAiChat } from "@src/store/slices/global";
import { EllieChatSource, mpEllieChatToggled } from "@src/utils/mixpanel";

/**
 * Tracks `chat_toggled` with `agent_name: ellie` for the experience-creation
 * agent's chat panel.
 *
 * Listens on `setAiChat` rather than the sparkle button's `onClick`
 * (`ExperienceStickyHeader`) so the close path — the panel's own X button, and
 * `AiChat`'s unmount cleanup — is covered by the same event. Two guards keep
 * the signal honest:
 *
 * - the `open` value must actually have changed, since `AiChat`'s effect
 *   re-dispatches `open: false` on unmount even when the panel is already
 *   closed, which would otherwise log phantom toggles;
 * - the panel must be in `experience-creation-single-agent` mode, since the
 *   same action drives the analytics / ui-actions chats.
 */
export function setupEllieChatMixPanelListener(
  middleware: AppListenerMiddleware
) {
  middleware.startListening({
    actionCreator: setAiChat,
    effect: (action, { getState, getOriginalState }) => {
      const entries = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      const openEntry = entries.find((entry) => entry.key === "open");
      if (!openEntry) {
        return;
      }

      const opened = Boolean(openEntry.value);
      if (getAiChatProps(getOriginalState()).open === opened) {
        return;
      }
      const { mode, initialContext } = getAiChatProps<{
        chatSource?: EllieChatSource;
      } | null>(getState());
      if (mode !== "experience-creation-single-agent") {
        return;
      }

      mpEllieChatToggled({
        experienceId: selectedExperience(getState())?.id,
        // Empty until the first turn commits a session — a merchant can open
        // and close the panel without ever sending a message.
        chatId: getAiChatSessionId(getState()) || undefined,
        chatSource: initialContext?.chatSource,
        opened,
      });
    },
  });
}
